/**
 * Table filters, in the shape Attio uses: pick a column, pick a condition,
 * give a value. Each filter becomes a chip you can click to edit or dismiss.
 *
 * Filters run on the SERVER, like sort and search. Filtering the loaded page
 * would quietly answer a different question — "which of these hundred rows
 * match" instead of "which rows match" — and on a 16,000-row object those are
 * not close to the same answer.
 *
 * Neither the condition list nor which conditions apply to which type is
 * decided here: both come from the records endpoint, which gets them from
 * api/_lib/attribute-types.ts. Offering a condition the query builder cannot
 * honour would produce a filter that silently matches nothing.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Filter, X, Plus, Check } from 'lucide-react';
import { Tag, type Attribute } from './EditableCell';

export interface FilterClause {
  attribute: string;
  operator: string;
  value: string;
  /**
   * Carried on the clause rather than looked up from the server's operator
   * list, so deciding whether a clause is complete needs nothing but the
   * clause. Looking it up would make the request depend on the response it
   * came from. The server ignores the extra key.
   */
  needsValue: boolean;
}

export interface OperatorDef {
  id: string;
  name: string;
  needsValue: boolean;
}

/** An attribute as the records endpoint serves it, with its allowed conditions. */
export type FilterableAttribute = Attribute & { operators?: string[] };

interface Props {
  attributes: FilterableAttribute[];
  operators: OperatorDef[];
  value: FilterClause[];
  onChange: (next: FilterClause[]) => void;
}

export function FilterBar({ attributes, operators, value, onChange }: Props) {
  const [adding, setAdding] = useState(false);

  const opsFor = (a: FilterableAttribute) =>
    operators.filter((o) => (a.operators ?? ['is']).includes(o.id));

  const set = (i: number, patch: Partial<FilterClause>) =>
    onChange(value.map((f, n) => (n === i ? { ...f, ...patch } : f)));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {value.map((f, i) => {
        const attr = attributes.find((a) => a.api_slug === f.attribute);
        if (!attr) return null;
        return (
          <FilterChip
            key={`${f.attribute}-${i}`}
            attr={attr}
            clause={f}
            operators={opsFor(attr)}
            onChange={(patch) => set(i, patch)}
            onRemove={() => onChange(value.filter((_, n) => n !== i))}
          />
        );
      })}

      <Popover open={adding} onOpenChange={setAdding}>
        <PopoverTrigger asChild>
          <Button
            variant={value.length ? 'ghost' : 'outline'}
            size="sm"
            className="h-7 gap-1.5 text-xs"
          >
            {value.length ? <Plus className="h-3 w-3" /> : <Filter className="h-3 w-3" />}
            {value.length ? 'Add filter' : 'Filter'}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          <ColumnPicker
            attributes={attributes}
            onPick={(a) => {
              const first = opsFor(a)[0];
              onChange([...value, {
                attribute: a.api_slug,
                operator: first?.id ?? 'is',
                value: '',
                needsValue: first?.needsValue ?? true,
              }]);
              setAdding(false);
            }}
          />
        </PopoverContent>
      </Popover>

      {value.length > 1 && (
        <Button
          variant="ghost" size="sm"
          className="h-7 text-xs text-muted-foreground"
          onClick={() => onChange([])}
        >
          Clear all
        </Button>
      )}
    </div>
  );
}

// ── Column picker ───────────────────────────────────────────────────────────

function ColumnPicker({
  attributes, onPick,
}: { attributes: FilterableAttribute[]; onPick: (a: FilterableAttribute) => void }) {
  return (
    <Command>
      <CommandInput placeholder="Filter on which column?" className="h-8 text-xs" />
      <CommandList className="max-h-64">
        <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">
          No column by that name.
        </CommandEmpty>
        <CommandGroup>
          {attributes.map((a) => (
            <CommandItem
              key={a.api_slug}
              value={`${a.label} ${a.api_slug}`}
              onSelect={() => onPick(a)}
              className="text-xs"
            >
              <span className="truncate">{a.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

// ── One filter chip ─────────────────────────────────────────────────────────

function FilterChip({
  attr, clause, operators, onChange, onRemove,
}: {
  attr: FilterableAttribute;
  clause: FilterClause;
  operators: OperatorDef[];
  onChange: (patch: Partial<FilterClause>) => void;
  onRemove: () => void;
}) {
  // A brand-new chip opens straight onto its value box: adding a filter and
  // then having to click it again to say what you are filtering for is a step
  // with no decision in it.
  const [open, setOpen] = useState(clause.needsValue && !clause.value);
  const op = operators.find((o) => o.id === clause.operator);
  const needsValue = op?.needsValue ?? true;
  // A chip with no value yet is incomplete rather than wrong — it is shown
  // dashed so it reads as unfinished, and the table ignores it until it is.
  const incomplete = needsValue && !clause.value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          'flex h-7 items-center gap-1 rounded-md border pl-2 pr-1 text-xs',
          incomplete ? 'border-dashed text-muted-foreground' : 'bg-accent/40'
        )}
      >
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1">
            <span className="font-medium">{attr.label}</span>
            <span className="text-muted-foreground">{op?.name ?? clause.operator}</span>
            {needsValue && (
              clause.value
                ? (attr.type === 'multi_select'
                    ? <Tag value={clause.value} />
                    : <span className="max-w-32 truncate font-medium">{clause.value}</span>)
                : <span className="italic">…</span>
            )}
          </button>
        </PopoverTrigger>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove filter on ${attr.label}`}
          className="rounded-sm p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      <PopoverContent align="start" className="w-64 p-0">
        <div className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {attr.label}
        </div>
        <Separator />
        <div className="py-1">
          {operators.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange({
                operator: o.id,
                needsValue: o.needsValue,
                // Dropping the value when moving to a standalone condition
                // keeps a stale term from reappearing if they switch back.
                ...(o.needsValue ? {} : { value: '' }),
              })}
              className={cn(
                'flex w-full items-center justify-between px-2 py-1 text-left text-xs hover:bg-accent',
                o.id === clause.operator && 'font-medium'
              )}
            >
              {o.name}
              {o.id === clause.operator && <Check className="h-3 w-3" />}
            </button>
          ))}
        </div>

        {needsValue && (
          <>
            <Separator />
            <div className="p-2">
              <ValueInput attr={attr} value={clause.value} onChange={(v) => onChange({ value: v })} />
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── The value ───────────────────────────────────────────────────────────────

/**
 * A fixed set of choices gets a list; anything else gets a box. Typing a
 * dropdown's value by hand is how you end up filtering for "Aproved".
 */
function ValueInput({
  attr, value, onChange,
}: { attr: FilterableAttribute; value: string; onChange: (v: string) => void }) {
  const choices = attr.options ?? null;

  if (choices?.length) {
    return (
      <div className="max-h-48 space-y-0.5 overflow-y-auto">
        {choices.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className={cn(
              'flex w-full items-center justify-between rounded-sm px-1.5 py-1 text-left text-xs hover:bg-accent',
              c === value && 'bg-accent'
            )}
          >
            {attr.type === 'multi_select' ? <Tag value={c} /> : <span className="truncate">{c}</span>}
            {c === value && <Check className="h-3 w-3 shrink-0" />}
          </button>
        ))}
      </div>
    );
  }

  if (attr.type === 'boolean') {
    return (
      <div className="flex gap-1">
        {[['true', 'Yes'], ['false', 'No']].map(([v, label]) => (
          <Button
            key={v}
            type="button" size="sm"
            variant={value === v ? 'default' : 'outline'}
            className="h-7 flex-1 text-xs"
            onClick={() => onChange(v)}
          >
            {label}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <Input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Value…"
      type={attr.type === 'number' || attr.type === 'currency' ? 'number'
        : attr.type === 'date' ? 'date' : 'text'}
      className="h-7 text-xs"
    />
  );
}

/** Only complete clauses are worth sending; the rest are still being written. */
export function usableFilters(filters: FilterClause[]): FilterClause[] {
  return filters.filter((f) => !f.needsValue || f.value !== '');
}
